import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderSlot as _renderSlot, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = ["id"]
const _hoisted_2 = { class: "g-gantt-bar-label" }
const _hoisted_3 = ["innerHTML"]
const _hoisted_4 = /*#__PURE__*/_createElementVNode("div", { class: "g-gantt-bar-handle-left" }, null, -1)
const _hoisted_5 = /*#__PURE__*/_createElementVNode("div", { class: "g-gantt-bar-handle-right" }, null, -1)

import { computed, ref, toRefs, watch, onMounted, inject } from "vue"

import useBarDragManagement from "../composables/useBarDragManagement"
import useTimePositionMapping from "../composables/useTimePositionMapping"
import useBarDragLimit from "../composables/useBarDragLimit"
import type { GanttBarObject } from "../types"
import provideEmitBarEvent from "../provider/provideEmitBarEvent"
import provideConfig from "../provider/provideConfig"
import { BAR_CONTAINER_KEY } from "../provider/symbols"

// eslint-disable-next-line no-undef

export default /*#__PURE__*/_defineComponent({
  __name: 'GGanttBar',
  props: {
    bar: {}
  },
  setup(__props: any) {

const props = __props
const emitBarEvent = provideEmitBarEvent()
const config = provideConfig()
const { rowHeight } = config

const { bar } = toRefs(props)
const { mapTimeToPosition, mapPositionToTime } = useTimePositionMapping()
const { initDragOfBar, initDragOfBundle } = useBarDragManagement()
const { setDragLimitsOfGanttBar } = useBarDragLimit()

const isDragging = ref(false)

const barConfig = computed(() => bar.value.ganttBarConfig)

function firstMousemoveCallback(e: MouseEvent) {
  barConfig.value.bundle != null ? initDragOfBundle(bar.value, e) : initDragOfBar(bar.value, e)
  isDragging.value = true
}

const prepareForDrag = () => {
  setDragLimitsOfGanttBar(bar.value)
  if (barConfig.value.immobile) {
    return
  }

  window.addEventListener("mousemove", firstMousemoveCallback, {
    once: true
  }) // on first mousemove event
  window.addEventListener(
    "mouseup",
    () => {
      // in case user does not move the mouse after mousedown at all
      window.removeEventListener("mousemove", firstMousemoveCallback)
      isDragging.value = false
    },
    { once: true }
  )
}

const barContainerEl = inject(BAR_CONTAINER_KEY)

const onMouseEvent = (e: MouseEvent) => {
  if (e.button != 0) return  // only care for left click
  e.preventDefault()
  if (e.type === "mousedown") {
    prepareForDrag()
  }
  const barContainer = barContainerEl?.value?.getBoundingClientRect()
  if (!barContainer) {
    return
  }
  const datetime = mapPositionToTime(e.clientX - barContainer.left)
  emitBarEvent(e, bar.value, datetime)
}

const { barStart, barEnd, width, chartStart, chartEnd, chartSize } = config

const xStart = ref(0)
const xEnd = ref(0)

onMounted(() => {
  watch(
    [bar, width, chartStart, chartEnd, chartSize.width],
    () => {
      xStart.value = mapTimeToPosition(bar.value[barStart.value])
      xEnd.value = mapTimeToPosition(bar.value[barEnd.value])
    },
    { deep: true, immediate: true }
  )
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    id: barConfig.value.id,
    class: _normalizeClass(['g-gantt-bar', barConfig.value.class || '']),
    style: _normalizeStyle({
      ...barConfig.value.style,
      position: 'absolute',
      top: `${_unref(rowHeight) * 0.1}px`,
      left: `${xStart.value}px`,
      width: `${xEnd.value - xStart.value}px`,
      height: `${_unref(rowHeight) * 0.8}px`,
      zIndex: isDragging.value ? 3 : 2
    }),
    onMousedown: onMouseEvent,
    onClick: onMouseEvent,
    onDblclick: onMouseEvent,
    onMouseenter: onMouseEvent,
    onMouseleave: onMouseEvent,
    onContextmenu: onMouseEvent
  }, [
    _createElementVNode("div", _hoisted_2, [
      _renderSlot(_ctx.$slots, "default", { bar: _unref(bar) }, () => [
        _createElementVNode("div", null, _toDisplayString(barConfig.value.label || ""), 1),
        (barConfig.value.html)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              innerHTML: barConfig.value.html
            }, null, 8, _hoisted_3))
          : _createCommentVNode("", true)
      ])
    ]),
    (barConfig.value.hasHandles)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          _hoisted_4,
          _hoisted_5
        ], 64))
      : _createCommentVNode("", true)
  ], 46, _hoisted_1))
}
}

})